import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Employer } from 'src/app/models/employer';
import { TranslationService } from 'src/app/services/translation.service';
import { CrisisCardComponent } from '../crisis-card/crisis-card.component';
import { TranslateDirective } from 'src/app/directives/translate.directive';

@Component({
  selector: 'app-crisis-cards-section',
  templateUrl: './crisis-cards-section.component.html',
  styleUrls: ['./crisis-cards-section.component.scss']
})
export class CrisisCardsSectionComponent implements OnInit, OnDestroy {

	@Input() data: any;
	@Input() employer: Employer;
	@ViewChildren(CrisisCardComponent) crisisCards: QueryList<CrisisCardComponent>;
	@ViewChildren(TranslateDirective) translateDirectives: QueryList<TranslateDirective>;

	private destroy$ = new Subject<void>();

	constructor(private translationService: TranslationService) {}

	ngOnInit(): void {
		// Listen for language changes
		this.translationService.currentLanguage$.pipe(
			takeUntil(this.destroy$)
		).subscribe(() => {
			if (this.employer) {
				this.translateCrisisCards();
			}
		});

		// Initial translation after view init
		setTimeout(() => this.translateCrisisCards(), 100);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	/**
	 * Translate crisis card content
	 */
	private translateCrisisCards(): void {
		if (!this.employer || !this.employer.employerID) {
			return;
		}

		const items: Array<{ id: string; text: string }> = [];

		// Collect from translate directives (section headers)
		if (this.translateDirectives) {
			this.translateDirectives.forEach(directive => {
				const id = directive.getId();
				const text = directive.getOriginalText();
				if (id && text) {
					items.push({ id, text });
				}
			});
		}

		// Collect translatable items from crisis cards
		if (this.crisisCards) {
			this.crisisCards.forEach((card) => {
				if (card.headTranslateId && card.head) {
					items.push({ id: card.headTranslateId, text: card.head });
				}
				if (card.bodyTranslateId && card.body) {
					items.push({ id: card.bodyTranslateId, text: card.body });
				}
			});
		}

		if (items.length === 0) {
			return;
		}

		// Get the page path from the employer or use a default
		const pagePath = '/crisis-support';

		// Call translation service
		this.translationService.translatePage(
			this.employer.employerID,
			pagePath,
			items
		).subscribe(translations => {
			// Apply translations to directives (section headers)
			if (this.translateDirectives) {
				this.translateDirectives.forEach(directive => {
					const id = directive.getId();
					if (translations[id]) {
						directive.applyTranslation(translations[id]);
					}
				});
			}

			// Apply translations to crisis cards
			if (this.crisisCards) {
				this.crisisCards.forEach((card) => {
					if (card.headTranslateId && translations[card.headTranslateId]) {
						card.applyHeadTranslation(translations[card.headTranslateId]);
					}
					if (card.bodyTranslateId && translations[card.bodyTranslateId]) {
						card.applyBodyTranslation(translations[card.bodyTranslateId]);
					}
				});
			}
		});
	}
}
